import { createContext, type ReactNode, useContext } from "react";

/**
 * Where the links of a shared screen lead. A screen rendered by both Returns
 * and the super admin must keep the user in the app that shows it: the
 * admin provides its own routes, Returns keeps the defaults.
 */
interface ScreenLinks {
  /** Product recommendations of a shop. */
  productRecommendations: (shopId: string) => string;
  /** PII lookup of a shop. */
  piiLookup: (shopId: string) => string;
  /**
   * A return request. Returns only: the admin has no return page and opens
   * it in a new tab (`isReturnInNewTab`).
   */
  returnRequest: (shopId: string, returnId: string) => string;
  isReturnInNewTab: boolean;
}

const RETURNS_SCREEN_LINKS: ScreenLinks = {
  productRecommendations: (shopId) =>
    `/shops/${shopId}/analytics/product-recommendations`,
  piiLookup: (shopId) => `/shops/${shopId}/pii-lookup`,
  returnRequest: (shopId, returnId) => `/shops/${shopId}/returns/${returnId}`,
  isReturnInNewTab: false,
};

const ScreenLinksContext = createContext<ScreenLinks>(RETURNS_SCREEN_LINKS);

export function ScreenLinksProvider({
  links,
  children,
}: {
  links: ScreenLinks;
  children: ReactNode;
}) {
  return (
    <ScreenLinksContext.Provider value={links}>
      {children}
    </ScreenLinksContext.Provider>
  );
}

export function useScreenLinks(): ScreenLinks {
  return useContext(ScreenLinksContext);
}
